// src/pages/CourseForm.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, storage } from '../firebase';
import {
  collection, getDocs, query, where, addDoc, doc, getDoc,
} from 'firebase/firestore';
import {
  ref, uploadBytes, getDownloadURL,
} from 'firebase/storage';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  TextField,
  Typography,
  Tabs,
  Tab,
  Chip,
  Tooltip,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EventIcon from '@mui/icons-material/Event';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SaveIcon from '@mui/icons-material/Save';
import ContentCopy from '@mui/icons-material/ContentCopy';
import LinkIcon from '@mui/icons-material/Link';

import {
  fetchDanceStyles,
  getDanceStyleGroups,
  getDanceStyleLabel,
  normalizeDanceStyleIds,
} from '../services/danceStylesService';

import {
  toBelgiumUTCISO,
  datetimeLocalToBelgiumUTCISO,
  formatForBelgiumDisplay,
} from '../utils/dateTimeUtils';

/* ====================== Options UI ====================== */
const DIFFICULTY_OPTIONS = ['Débutant', 'Intermédiaire', 'Avancé', 'Tous niveaux'];
const AUDIENCE_OPTIONS = ['Enfants', 'Ados', 'Adultes', 'Seniors'];
const DURATION_OPTIONS = [
  { value: 30,  label: '30 minutes' },
  { value: 60,  label: '1 heure' },
  { value: 90,  label: '1h30' },
  { value: 120, label: '2 heures' },
  { value: 150, label: '2h30' },
  { value: 180, label: '3 heures' },
];

function CourseForm() {
  const navigate = useNavigate();
  const [langTab, setLangTab] = useState('fr');

  const [courseData, setCourseData] = useState({
    title: '',
    description_i18n: { fr: '', en: '', nl: '' },
    description: '',
    difficultyLevels: [],
    audiences: [],
    danceStyles: [],
    maxStudents: 10,
    recurrence: 'none',
    teachers: [],
    dates: [],
    startDate: '',
    endDate: '',
    startTime: '18:00',
    endTime: '19:00',
    duration: 60,
    daysOfWeek: [],
    imageUrl: '',
    location: { name: '', address: '' },
    isNewCourse: false,
    isFull: false,
  });

  const [teachers, setTeachers] = useState([]);
  const [danceStyleOptions, setDanceStyleOptions] = useState([]);
  const [danceStylesLoading, setDanceStylesLoading] = useState(true);
  const [imageFile, setImageFile] = useState(null);
  const [imageSource, setImageSource] = useState('upload'); // 'url' ou 'upload'
  const [imageUrlInput, setImageUrlInput] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [newDate, setNewDate] = useState('');
  const [timeMode, setTimeMode] = useState('duration');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const transStatus = useMemo(() => {
    const make = (l) => {
      const complete = !!courseData.description_i18n?.[l];
      return { complete, missing: complete ? 0 : 1, total: 1 };
    };
    return { en: make('en'), nl: make('nl') };
  }, [courseData.description_i18n]);

  /* -------- Chargement des styles de danse -------- */
  useEffect(() => {
    (async () => {
      try {
        const styles = await fetchDanceStyles();
        setDanceStyleOptions(styles);
      } catch (e) {
        console.error(e);
        setError('Erreur lors du chargement des styles de danse');
      } finally {
        setDanceStylesLoading(false);
      }
    })();
  }, []);

  const danceStyleGroups = useMemo(
    () => getDanceStyleGroups(danceStyleOptions),
    [danceStyleOptions]
  );

  const danceStyleLabel = (styleId) =>
    getDanceStyleLabel(danceStyleOptions, styleId);

  const handleDanceStylesChange = (event) => {
    const selected = typeof event.target.value === 'string'
      ? event.target.value.split(',')
      : event.target.value;

    const normalized = normalizeDanceStyleIds(selected);
    const withParents = [...normalized];

    normalized.forEach((styleId) => {
      const style = danceStyleOptions.find((item) => item.id === styleId);
      if (style?.parentId && !withParents.includes(style.parentId)) {
        withParents.push(style.parentId);
      }
    });

    setCourseData((previous) => ({
      ...previous,
      danceStyles: withParents,
    }));
  };

  /* -------- Chargement des professeurs -------- */
  useEffect(() => {
    (async () => {
      try {
        const usersQ = query(collection(db, 'users'), where('role', '==', 'Professeur'));
        const usersSnap = await getDocs(usersQ);

        const list = await Promise.all(
          usersSnap.docs.map(async (userDoc) => {
            try {
              const tDoc = await getDoc(doc(db, 'teachers', userDoc.id));
              return {
                id: userDoc.id,
                ...userDoc.data(),
                ...(tDoc.exists() ? tDoc.data() : {}),
              };
            } catch {
              return { id: userDoc.id, ...userDoc.data() };
            }
          })
        );

        setTeachers(list);
      } catch (e) {
        console.error(e);
        setError('Erreur lors du chargement des professeurs');
      }
    })();
  }, []);

  /* -------- Handlers -------- */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setCourseData(prev => ({ ...prev, [name]: value }));
  };

  const handleLangChange = (field, value) => {
    setCourseData(prev => {
      const next = { ...prev };
      next[`${field}_i18n`] = { ...prev[`${field}_i18n`], [langTab]: value };
      if (langTab === 'fr') {
        next[field] = value;
      }
      return next;
    });
  };

  const handleLocationChange = (e) => {
    const { name, value } = e.target;
    setCourseData(prev => ({ ...prev, location: { ...prev.location, [name]: value } }));
  };
  
  const handleTeacherChange = (event) => {
    const { value } = event.target;
    setCourseData(prev => ({ ...prev, teachers: typeof value === 'string' ? value.split(',') : value }));
  };

  const copyFromFRToLang = (lang) => {
    setCourseData(prev => ({
      ...prev,
      description_i18n: { ...prev.description_i18n, [lang]: prev.description_i18n.fr || prev.description || '' }
    }));
  };

  const copyFRToAllEmpty = () => {
    setCourseData(prev => ({
      ...prev,
      description_i18n: {
        fr: prev.description_i18n.fr || prev.description || '',
        en: prev.description_i18n.en || prev.description_i18n.fr || prev.description || '',
        nl: prev.description_i18n.nl || prev.description_i18n.fr || prev.description || ''
      }
    }));
  };

  useEffect(() => {
    if (timeMode !== 'duration' || !courseData.startTime) return;
    const [h, m] = courseData.startTime.split(':').map(Number);
    const start = new Date(); start.setHours(h, m, 0, 0);
    const end = new Date(start.getTime() + (Number(courseData.duration) || 0) * 60000);
    const hh = String(end.getHours()).padStart(2, '0');
    const mm = String(end.getMinutes()).padStart(2, '0');
    setCourseData(prev => ({ ...prev, endTime: `${hh}:${mm}` }));
  }, [courseData.startTime, courseData.duration, timeMode]);

  /* -------- Gestion des dates -------- */
  const addManualDate = () => {
    if (!newDate) return;
    const dateObj = new Date(newDate);
    const dayOfWeek = dateObj.toLocaleDateString('fr-FR', { weekday: 'long' });
    const formattedDay = dayOfWeek.charAt(0).toUpperCase() + dayOfWeek.slice(1);
    setCourseData(prev => ({
      ...prev,
      dates: [
        ...prev.dates,
        {
          date: datetimeLocalToBelgiumUTCISO(newDate),
          day: formattedDay,
          startTime: prev.startTime,
          endTime: prev.endTime,
          duration: prev.duration,
        },
      ],
    }));
    setNewDate('');
  };

  const removeManualDate = (idx) => {
    setCourseData(prev => {
      const copy = [...prev.dates];
      copy.splice(idx, 1);
      return { ...prev, dates: copy };
    });
  };

  const handleDayToggle = (day) => {
    setCourseData(prev => {
      const days = [...prev.daysOfWeek];
      const i = days.indexOf(day);
      if (i > -1) days.splice(i, 1); else days.push(day);
      return { ...prev, daysOfWeek: days };
    });
  };

  const generateRecurringDates = () => {
    const dates = [];
    const start = new Date(courseData.startDate);
    const end = new Date(courseData.endDate);
    if (!(courseData.startDate && courseData.endDate) || start > end) {
      setError('Veuillez saisir une période valide'); return;
    }
    if (!courseData.startTime) {
      setError("Veuillez définir l'heure de début"); return;
    }
    const wantedDays = courseData.daysOfWeek;

    const cur = new Date(start);
    while (cur <= end) {
      const day = cur.toLocaleDateString('fr-FR', { weekday: 'long' });
      const dayLabel = day.charAt(0).toUpperCase() + day.slice(1);
      if (wantedDays.includes(dayLabel)) {
        const ymd = cur.toISOString().slice(0, 10);
        dates.push({
          date: toBelgiumUTCISO(ymd, courseData.startTime),
          day: dayLabel,
          startTime: courseData.startTime,
          endTime: courseData.endTime,
          duration: courseData.duration,
        });
      }
      cur.setDate(cur.getDate() + 1);
    }
    setCourseData(prev => ({ ...prev, dates }));
  };

  /* -------- Upload image CORRIGÉ avec option URL -------- */
  const handleImageUpload = async () => {
    // Si source = URL, retourner l'URL saisie
    if (imageSource === 'url') {
      return imageUrlInput.trim();
    }

    // Si source = upload mais pas de fichier, retourner vide
    if (!imageFile) {
      return '';
    }

    try {
      const safeName = imageFile.name
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9._-]/g, '_');

      const storageRef = ref(storage, `course-images/${Date.now()}_${safeName}`);
      
      const snapshot = await uploadBytes(storageRef, imageFile);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return downloadURL;
    } catch (e) {
      console.error('Erreur upload image:', e);
      setError("Erreur lors du téléchargement de l'image. Le cours sera créé sans image.");
      return '';
    }
  };

  /* -------- Submit CORRIGÉ -------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); 
    setSuccess('');

    // Validation
    if (!courseData.title || !courseData.description) {
      setError('Veuillez remplir tous les champs obligatoires'); 
      return;
    }
    if (courseData.recurrence === 'none' && !courseData.startDate) {
      setError('Veuillez sélectionner une date'); 
      return;
    }
    if (!courseData.location.name || !courseData.location.address) {
      setError("Veuillez renseigner le nom et l'adresse du lieu"); 
      return;
    }

    try {
      // Upload de l'image
      const uploadedUrl = await handleImageUpload();

      // Gestion des dates
      let finalDates = [...courseData.dates];
      if (courseData.recurrence === 'none' && courseData.startDate) {
        const dayOfWeek = new Date(courseData.startDate).toLocaleDateString('fr-FR', { weekday: 'long' });
        const formattedDay = dayOfWeek.charAt(0).toUpperCase() + dayOfWeek.slice(1);
        finalDates = [{
          date: toBelgiumUTCISO(courseData.startDate, courseData.startTime),
          day: formattedDay,
          startTime: courseData.startTime,
          endTime: courseData.endTime,
          duration: courseData.duration,
        }];
      }

      // Formatage des professeurs
      const teacherObjects = courseData.teachers.map(id => {
        const t = teachers.find(x => x.id === id) || {};
        const fullName = `${t.firstName || ''} ${t.lastName || ''}`.trim();
        return {
          id,
          firstName: t.firstName || '',
          lastName: t.lastName || '',
          pseudo: t.pseudo || fullName,
        };
      });

      // Création du cours
      await addDoc(collection(db, 'dance_courses'), {
        title: courseData.title,
        description: courseData.description,
        description_i18n: courseData.description_i18n,
        difficultyLevels: courseData.difficultyLevels,
        audiences: courseData.audiences,
        danceStyles: normalizeDanceStyleIds(courseData.danceStyles),
        maxStudents: Number(courseData.maxStudents) || 0,
        recurrence: courseData.recurrence,
        duration: courseData.duration,
        startDate: courseData.startDate || '',
        endDate: courseData.endDate || '',
        startTime: courseData.startTime || '',
        endTime: courseData.endTime || '',
        daysOfWeek: courseData.daysOfWeek || [],
        teachers: teacherObjects,
        dates: finalDates,
        imageUrl: uploadedUrl,
        location: { 
          name: courseData.location.name, 
          address: courseData.location.address 
        },
        isNewCourse: !!courseData.isNewCourse,
        isFull: !!courseData.isFull,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });

      setSuccess('Cours créé avec succès !');
      setTimeout(() => navigate('/courses'), 1200);
    } catch (err) {
      console.error('Erreur création cours:', err);
      setError('Erreur lors de la création du cours: ' + err.message);
    }
  };

  const currentDescription = courseData.description_i18n[langTab] || '';

  return (
    <div style={{ padding: 20, maxWidth: 900, margin: '0 auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
        <IconButton onClick={() => navigate('/courses')}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ ml: 1, fontWeight: 'bold' }}>
          Créer un cours
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

      <form onSubmit={handleSubmit} noValidate>
        <Paper elevation={1} sx={{ mb: 3, p: 3, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', color: '#1976d2' }}>
            Informations du cours
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField fullWidth label="Titre du cours *" name="title" value={courseData.title} onChange={handleChange} required />
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <Tabs value={langTab} onChange={(e, v) => setLangTab(v)} sx={{ mb: 1 }}>
                  <Tab value="fr" label="Français" />
                  <Tab value="en" label="English" />
                  <Tab value="nl" label="Nederlands" />
                </Tabs>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                  {langTab !== 'fr' && (
                    <Tooltip title={`Copier depuis FR vers ${langTab.toUpperCase()}`}>
                      <Button variant="outlined" size="small" startIcon={<ContentCopy />}
                        onClick={() => copyFromFRToLang(langTab)}>
                        Copier depuis FR
                      </Button>
                    </Tooltip>
                  )}
                  <Tooltip title="Remplir EN & NL avec FR (seulement les champs vides)">
                    <Button variant="text" size="small" startIcon={<ContentCopy />}
                      onClick={copyFRToAllEmpty}>
                      Remplir vides EN/NL
                    </Button>
                  </Tooltip>

                  <Chip size="small" label={`EN ${transStatus.en.total - transStatus.en.missing}/${transStatus.en.total}`}
                    color={transStatus.en.complete ? 'success' : 'default'} />
                  <Chip size="small" label={`NL ${transStatus.nl.total - transStatus.nl.missing}/${transStatus.nl.total}`}
                    color={transStatus.nl.complete ? 'success' : 'default'} />
                </Box>
              </Box>

              <TextField 
                fullWidth 
                label={`Description (${langTab.toUpperCase()}) ${langTab === 'fr' ? '*' : ''}`} 
                multiline 
                rows={3} 
                value={currentDescription}
                onChange={(e) => handleLangChange('description', e.target.value)}
                required={langTab === 'fr'}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Niveau(x) *</InputLabel>
                <Select
                  multiple
                  label="Niveau(x) *"
                  value={courseData.difficultyLevels}
                  onChange={(e) => setCourseData(prev => ({ ...prev, difficultyLevels: e.target.value }))}
                  renderValue={(selected) => selected.join(', ')}
                >
                  {DIFFICULTY_OPTIONS.map(level => (
                    <MenuItem key={level} value={level}>
                      <Checkbox checked={courseData.difficultyLevels.includes(level)} />
                      <ListItemText primary={level} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Public *</InputLabel>
                <Select
                  multiple
                  label="Public *"
                  value={courseData.audiences}
                  onChange={(e) => setCourseData(prev => ({ ...prev, audiences: e.target.value }))}
                  renderValue={(selected) => selected.join(', ')}
                >
                  {AUDIENCE_OPTIONS.map(a => (
                    <MenuItem key={a} value={a}>
                      <Checkbox checked={courseData.audiences.includes(a)} />
                      <ListItemText primary={a} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth disabled={danceStylesLoading}>
                <InputLabel>Style(s) de danse</InputLabel>
                <Select
                  multiple
                  label="Style(s) de danse"
                  value={courseData.danceStyles || []}
                  onChange={handleDanceStylesChange}
                  renderValue={(selected) => selected.map(danceStyleLabel).join(', ')}
                >
                  {danceStyleGroups.map((category) => [
                    <MenuItem key={category.id} value={category.id} sx={{ fontWeight: 700 }}>
                      <Checkbox checked={(courseData.danceStyles || []).includes(category.id)} />
                      <ListItemText primary={category.label} secondary="Catégorie" />
                    </MenuItem>,
                    ...category.children.map((child) => (
                      <MenuItem key={child.id} value={child.id} sx={{ pl: 4 }}>
                        <Checkbox checked={(courseData.danceStyles || []).includes(child.id)} />
                        <ListItemText primary={child.label} secondary={category.label} />
                      </MenuItem>
                    )),
                  ])}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary">
                {danceStylesLoading
                  ? 'Chargement des styles...'
                  : 'La catégorie parente est ajoutée automatiquement lorsque vous choisissez une sous-catégorie.'}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                inputProps={{ min: 1 }}
                label="Nombre max d'étudiants"
                name="maxStudents"
                value={courseData.maxStudents}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!courseData.isNewCourse}
                    onChange={(e) => setCourseData(prev => ({ ...prev, isNewCourse: e.target.checked }))}
                  />
                }
                label="🆕 Nouveau cours (éligible à l'offre spéciale été 199€)"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!courseData.isFull}
                    onChange={(e) => setCourseData(prev => ({ ...prev, isFull: e.target.checked }))}
                  />
                }
                label="🔒 Cours complet (bloque les inscriptions, affiche la liste d'attente)"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="teachers-select-label">Professeur(s)</InputLabel>
                <Select
                  multiple
                  labelId="teachers-select-label"
                  input={<OutlinedInput label="Professeur(s)" />}
                  name="teachers"
                  value={courseData.teachers}
                  onChange={handleTeacherChange}
                  renderValue={(selected) =>
                    selected.map(id => {
                      const t = teachers.find(tt => tt.id === id);
                      return t ? `${t.firstName || ''} ${t.lastName || ''}`.trim() : id;
                    }).join(', ')
                  }
                >
                  {teachers.map(t => (
                    <MenuItem key={t.id} value={t.id}>
                      <Checkbox checked={courseData.teachers.includes(t.id)} />
                      <ListItemText primary={`${t.firstName || ''} ${t.lastName || ''}`.trim() || t.pseudo || t.id} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        <Paper elevation={1} sx={{ mb: 3, p: 3, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', color: '#1976d2' }}>
            Lieu du cours
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nom du lieu *"
                name="name"
                value={courseData.location.name}
                onChange={handleLocationChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Adresse *"
                name="address"
                value={courseData.location.address}
                onChange={handleLocationChange}
                required
              />
            </Grid>
          </Grid>
        </Paper>

        <Paper elevation={1} sx={{ mb: 3, p: 3, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', color: '#1976d2' }}>
            <EventIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Configuration des dates
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Récurrence</InputLabel>
                <Select name="recurrence" label="Récurrence" value={courseData.recurrence} onChange={handleChange}>
                  <MenuItem value="none">Date unique</MenuItem>
                  <MenuItem value="weekly">Hebdomadaire</MenuItem>
                  <MenuItem value="monthly">Mensuel</MenuItem>
                  <MenuItem value="multiple">Dates multiples</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {courseData.recurrence === 'none' && (
              <>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth type="date" label="Date du cours *" InputLabelProps={{ shrink: true }} 
                    value={courseData.startDate} 
                    onChange={(e) => setCourseData(prev => ({ ...prev, startDate: e.target.value }))} 
                    required 
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth type="time" label="Heure de début *" InputLabelProps={{ shrink: true }} 
                    value={courseData.startTime} 
                    onChange={(e) => setCourseData(prev => ({ ...prev, startTime: e.target.value }))} 
                    required 
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    select fullWidth label="Durée *"
                    value={courseData.duration}
                    onChange={(e) => setCourseData(prev => ({ ...prev, duration: parseInt(e.target.value, 10) }))}
                    required
                  >
                    {DURATION_OPTIONS.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </>
            )}

            {['weekly', 'monthly'].includes(courseData.recurrence) && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>Jours de la semaine *</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {['Lundi','Mardi','Mercredi','Jeudi','Vendredi','Samedi','Dimanche'].map(day => (
                      <Button key={day} variant={courseData.daysOfWeek.includes(day) ? 'contained' : 'outlined'} 
                        size="small" onClick={() => handleDayToggle(day)}>
                        {day}
                      </Button>
                    ))}
                  </Box>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField fullWidth type="date" label="Date de début *" InputLabelProps={{ shrink: true }} 
                    value={courseData.startDate} 
                    onChange={(e) => setCourseData(prev => ({ ...prev, startDate: e.target.value }))} 
                    required 
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth type="date" label="Date de fin *" InputLabelProps={{ shrink: true }} 
                    value={courseData.endDate} 
                    onChange={(e) => setCourseData(prev => ({ ...prev, endDate: e.target.value }))} 
                    required 
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth type="time" label="Heure de début *" InputLabelProps={{ shrink: true }} 
                    value={courseData.startTime} 
                    onChange={(e) => setCourseData(prev => ({ ...prev, startTime: e.target.value }))} 
                    required 
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box display="flex" gap={1} sx={{ mt: 1 }}>
                    <Button variant={timeMode === 'duration' ? 'contained' : 'outlined'} 
                      onClick={() => setTimeMode('duration')}>Par durée</Button>
                    <Button variant={timeMode === 'range' ? 'contained' : 'outlined'} 
                      onClick={() => setTimeMode('range')}>Par plage</Button>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  {timeMode === 'duration' ? (
                    <TextField
                      select fullWidth label="Durée *"
                      value={courseData.duration}
                      onChange={(e) => setCourseData(prev => ({ ...prev, duration: parseInt(e.target.value, 10) }))}
                      required
                    >
                      {DURATION_OPTIONS.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </TextField>
                  ) : (
                    <TextField fullWidth type="time" label="Heure de fin *" InputLabelProps={{ shrink: true }} 
                      value={courseData.endTime} 
                      onChange={(e) => setCourseData(prev => ({ ...prev, endTime: e.target.value }))} 
                      required 
                    />
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Button variant="contained" startIcon={<EventIcon />} 
                    onClick={generateRecurringDates} 
                    disabled={!courseData.startDate || !courseData.endDate || courseData.daysOfWeek.length === 0}>
                    Générer les dates
                  </Button>
                </Grid>
              </>
            )}

            {courseData.recurrence === 'multiple' && (
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
                  <TextField fullWidth type="datetime-local" label="Ajouter une date" 
                    value={newDate} 
                    onChange={(e) => setNewDate(e.target.value)} 
                    InputLabelProps={{ shrink: true }} 
                  />
                  <Button variant="contained" startIcon={<AddIcon />} 
                    onClick={addManualDate} disabled={!newDate}>
                    Ajouter
                  </Button>
                </Box>
                {courseData.dates.length > 0 && (
                  <Paper variant="outlined" sx={{ p: 1, maxHeight: 180, overflow: 'auto' }}>
                    {courseData.dates.map((d, i) => (
                      <Box key={i} display="flex" justifyContent="space-between" alignItems="center" sx={{ py: 1, borderBottom: '1px solid #eee' }}>
                        <Typography variant="body2">{formatForBelgiumDisplay(d.date)}</Typography>
                        <IconButton size="small" onClick={() => removeManualDate(i)} color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Paper>
                )}
              </Grid>
            )}
          </Grid>
        </Paper>

        <Paper elevation={1} sx={{ mb: 3, p: 3, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#1976d2' }}>
            Image du cours
          </Typography>

          <FormControl component="fieldset" sx={{ mb: 2 }}>
            <RadioGroup
              row
              value={imageSource}
              onChange={(e) => setImageSource(e.target.value)}
            >
              <FormControlLabel value="url" control={<Radio />} label="URL d'image" />
              <FormControlLabel value="upload" control={<Radio />} label="Télécharger une image" />
            </RadioGroup>
          </FormControl>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              {imageSource === 'url' ? (
                <TextField
                  fullWidth
                  label="URL de l'image"
                  value={imageUrlInput}
                  onChange={(e) => setImageUrlInput(e.target.value)}
                  placeholder="https://example.com/image.jpg"
                  InputProps={{
                    startAdornment: <LinkIcon sx={{ mr: 1, color: 'action.active' }} />,
                  }}
                />
              ) : (
                <Button variant="outlined" component="label" fullWidth startIcon={<CloudUploadIcon />}>
                  Télécharger une image
                  <input 
                    hidden 
                    type="file" 
                    accept="image/*" 
                    onChange={(e) => setImageFile(e.target.files?.[0] ?? null)} 
                  />
                </Button>
              )}

              {uploadProgress > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2">Progression: {uploadProgress}%</Typography>
                  <LinearProgress variant="determinate" value={uploadProgress} sx={{ mt: 1 }} />
                </Box>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              {imageSource === 'upload' && imageFile ? (
                <img src={URL.createObjectURL(imageFile)} alt="Aperçu" 
                  style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8, border: '1px solid #ddd' }} />
              ) : imageSource === 'url' && imageUrlInput ? (
                <img src={imageUrlInput} alt="Aperçu URL" 
                  style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8, border: '1px solid #ddd', objectFit: 'cover' }} 
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              ) : (
                <Box sx={{ bgcolor: '#f5f5f5', height: 150, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 2, border: '1px dashed #ddd' }}>
                  <Typography variant="body2" color="textSecondary">Aperçu de l'image</Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        </Paper>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Button type="submit" variant="contained" size="large" startIcon={<SaveIcon />} 
            sx={{ py: 1.5, px: 6, fontSize: '1.1rem', fontWeight: 'bold', boxShadow: 2 }}>
            Créer le cours
          </Button>
        </Box>
      </form>
    </div>
  );
}

export default CourseForm;